// DashboardAccountSetting.js
import { LitElement, html, css } from 'lit';
import { FetchMixin } from '../FetchMixin';
import './DashboardModalWindow.js';
import './MessageBox.js';
import alertStore from '../AlertStore.js';
import { setAlert } from '../AlertSlice.js';
import userSettingStore from '../UserSettingStore.js';
import { loadUserSettingData, setAiCodingEnabled, setUserGeminiApiKeyRegistered } from '../UserSettingSlice.js';

class DashboardAccountSetting extends FetchMixin(LitElement) {
  static styles = css`
      :host {
        width: 100%;
        height: 100%;
        display: none;
        left: 50%;
        top: 50%;
        transform: translateX(-50%) translateY(-50%);
        position: fixed;
        background: rgba(0, 0, 0, 0.6);
        z-index: 1000;
        user-select: none;
        --max-modal-width: 420px;
      }
      :host([visible]) {
        display: block;
      }
      .component-content {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .setting-content {
        width: 90%;
        max-width: var(--max-modal-width);
        background: white;
        border-radius: 10px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        padding: 20px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 20px;
      }
      .setting-title {
        font-size: 18px;
        font-weight: bold;
      }
      .setting-description {
        font-size: 12px;
        text-align: left;
        margin-bottom: 15px;
        line-height: 1.5;
        color: #555;
      }
      .setting-description a {
        color: #007bff;
        text-decoration: none;
      }
      .setting-description a:hover {
        text-decoration: underline;
      }
      .setting-description i {
        font-size: 0.8em;
        margin-left: 4px;
      }
      .setting-button {
        color: white;
        border: none;
        border-radius: 4px;
        padding: 10px 20px;
        cursor: pointer;
        font-size: 14px;
        text-align: center;
      }
      .setting-button:hover {
        filter: brightness(90%);
      }
      .setting-button:disabled {
        background-color: #bdbdbd;
        color: black;
        cursor: not-allowed;
      }
      .setting-options {
        width: 100%;
        display: flex;
        flex-direction: row;
        gap: 10px;
        align-items: center;
        justify-content: space-between;
      }
      .switch {
        position: relative;
        display: inline-block;
        width: 60px;
        height: 34px;
      }
      .switch input { 
        opacity: 0;
        width: 0;
        height: 0;
      }
      .slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #ccc;
        -webkit-transition: .4s;
        transition: .4s;
        border-radius: 34px;
      }
      .slider:before {
        position: absolute;
        content: "";
        height: 26px;
        width: 26px;
        left: 4px;
        bottom: 4px;
        background-color: white;
        -webkit-transition: .4s;
        transition: .4s;
        border-radius: 50%;
      }
      input:checked + .slider {
        background-color: #22c72d;
      }
      input:focus + .slider {
        box-shadow: 0 0 1px #22c72d;
      }
      input:checked + .slider:before {
        -webkit-transform: translateX(26px);
        -ms-transform: translateX(26px);
        transform: translateX(26px);
      }
      .setting-option {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .setting-option label {
        font-size: 14px;
        color: #333;
      }
      .setting-section {
        margin-top: 20px;
        padding-top: 20px;
        border-top: 1px solid #e0e0e0;
        width: 100%;
      }
      .setting-section-title {
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 10px;
        text-align: left;
      }
      .api-key-form,
      .api-key-display {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
      }
      .api-key-form input {
        flex-grow: 1;
        padding: 8px;
        border: 1px solid #ccc;
        border-radius: 4px;
      }
      .api-key-display {
        background-color: #f0f0f0;
        padding-top: 10px;
        padding-bottom: 10px;
        border-radius: 4px;
        border: 1px solid #ddd;
        justify-content: space-between;
      }
      .api-key-status {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        color: #333;
        margin-left: 15px;
      }
      .api-key-status i {
        color: #28a745;
        font-size: 1.2em;
      }
      .save-api-key-button,
      .delete-api-key-button {
        padding: 8px 12px;
        white-space: nowrap;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        text-align: center;
        transition: background-color 0.2s;
      }
      .save-api-key-button {
        background-color: #007bff;
      }
      .save-api-key-button:hover {
        background-color: #0056b3;
      }
      .delete-api-key-button {
        background-color: #dc3545; /* 赤色 */
        margin-right: 15px;
      }
      .delete-api-key-button:hover {
        background-color: #c82333; /* ホバー色も赤系に */
      }
      .caution {
        font-size: 50px;
        display: flex;
        margin: 0;
        justify-content: space-around;
        color: #e7c200;
      }
      .confirm-ok-button {
        color: white;
        background: #ff4242;
        border: 1px solid;
      }
      .confirm-cancel-button {
          color: #7171fd;
          background: white;
          border: 1px solid;
      }
      .confirm-ok-button:hover {
        background: #ff4242 !important;
      }
      .confirm-cancel-button:hover {
        background-color: #ffffff54 !important;
        color: black;
      }
  `;

  static get properties() {
    return {
      visible: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.visible = false;
    this.unsubscribe = userSettingStore.subscribe(() => this.requestUpdate());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  async firstUpdated() {
    const fontAwasomeStyle = document.querySelector(
      "link[rel='stylesheet'][data-name='fontawesomev6']"
    );
    const fontAwasomeStyleClone = fontAwasomeStyle.cloneNode(true);
    fontAwasomeStyleClone.removeAttribute('disabled');
    this.shadowRoot.insertBefore(fontAwasomeStyleClone, this.shadowRoot.firstChild);

    // `fetchAccountInfo`は`show`メソッドから呼び出されるのでここでは不要
  }

  updated(changedProperties) {
    if (changedProperties.has('visible')) {
      this.visible ? this.setAttribute('visible', '') : this.removeAttribute('visible');
    }
  }

  async show() {
    this.visible = true;
    await this.fetchAccountInfo();
  }

  hide() {
    this.visible = false;
    this.remove();
  }

  async fetchAccountInfo() {
    const endpoint = process.env.USER_SETTING_ENDPOINT;
    const options = {
      method: 'GET',
    };
    try {
      const json = await this.fetchWithAuth(endpoint, options);
      if (json.details && json.details.userSetting) {
        userSettingStore.dispatch(loadUserSettingData(json.details.userSetting));
      }
    } catch (error) {
      console.error('Error fetching account info:', error);
    } finally {
      const content = this.shadowRoot.querySelector('.setting-content');
      content.style.display = 'flex';
    }
  }

  async fetchUpdateAccountInfo(aiCodingValue) {
    const endpoint = process.env.USER_SETTING_ENDPOINT;
    const options = {
      method: 'POST',
      body: JSON.stringify({ aiCodingEnabled: aiCodingValue })
    };
    return this.fetchWithAuth(endpoint, options);
  }

  async fetchSaveUserGeminiApiKey(apiKey) {
    const endpoint = process.env.USER_GEMINI_API_KEY_ENDPOINT;
    const options = {
      method: 'POST',
      body: JSON.stringify({ geminiApiKey: apiKey }),
    };
    return this.fetchWithAuth(endpoint, options);
  }

  async fetchDeleteUserGeminiApiKey() {
    const endpoint = process.env.USER_GEMINI_API_KEY_ENDPOINT;
    const options = { method: 'DELETE' };
    return this.fetchWithAuth(endpoint, options);
  }

  async handleSaveApiKey() {
    const apiKeyInput = this.shadowRoot.getElementById('gemini-api-key');
    if (!apiKeyInput || !apiKeyInput.value.trim()) {
      alertStore.dispatch(setAlert('APIキーを入力してください。'));
      return;
    }
    try {
      await this.fetchSaveUserGeminiApiKey(apiKeyInput.value.trim());
      userSettingStore.dispatch(setUserGeminiApiKeyRegistered(true));
      alertStore.dispatch(setAlert('APIキーを登録しました。'));
      apiKeyInput.value = '';
    } catch (error) {
      console.error('Failed to save API key:', error);
    }
  }

  async handleDeleteApiKey() {
    if (confirm('登録されているAPIキーを本当に削除しますか？')) {
      try {
        await this.fetchDeleteUserGeminiApiKey();
        userSettingStore.dispatch(setUserGeminiApiKeyRegistered(false));
        alertStore.dispatch(setAlert('APIキーを削除しました。'));
      } catch (error) {
        console.error('Failed to delete API key:', error);
      }
    }
  }

  aiCodingChange(event) {
    const newAiCodingEnabled = event.target.checked;

    if (newAiCodingEnabled) {
      userSettingStore.dispatch(setAiCodingEnabled(true));
      this.showAICodingConfirm(true);
    } else {
      this.updateAICodingOnServer(false);
    }
  }

  async updateAICodingOnServer(aiCodingValue) {
    try {
      await this.fetchUpdateAccountInfo(aiCodingValue);
      await this.fetchAccountInfo();
    } catch (error) {
      alertStore.dispatch(setAlert(error.message || 'アカウント情報の更新に失敗しました。'));
      console.error('Error updating account info:', error);
      await this.fetchAccountInfo();
    }
  }

  showAICodingConfirm(aiCoding) {
    const modal = document.createElement('dashboard-modal-window');
    modal.id = 'aiCodingConfirmModal';
    modal.closeOnClickOutside = false;

    modal.innerHTML = `
      <span slot="header-title">確認</span>
      <div slot="content">
        <p>AI機能はGoogleのGemini APIを使用しています。<br/><a href="https://ai.google.dev/gemini-api/terms?hl=ja#age-requirements" target="_blank">利用規約</a>により１８歳未満の利用はできません</p>  
        <p>あなたは18歳以上ですか？</p>
      </div>
      <span slot="submit-button-text">はい</span> 
      <span slot="cancel-button-text">いいえ</span> 
    `;

    this.shadowRoot.appendChild(modal);
    modal.show();

    modal.addEventListener('modal-submit', async () => {
      this.updateAICodingOnServer(true);
      modal.hide();
      modal.remove();
    });

    modal.addEventListener('modal-cancel', () => {
      userSettingStore.dispatch(setAiCodingEnabled(false));
      modal.hide();
      modal.remove();
    });
  }

  async fetchDeleteAccountInfo() {
    // const endpoint = process.env.ACCOUNT_ENDPOINT;
    // const options = {
    //   method: 'DELETE',
    // };
    // const json = await this.fetchWithAuth(endpoint, options);
    // if (json.code !== 200) {
    //   return Promise.reject(json);
    // }
    // return Promise.resolve(json);
    return Promise.resolve({ code: 200 });
  }

  async handleDeleteAccount() {
    const messageBox = this.shadowRoot.querySelector('#delete-account-confirm');
    const ret = await messageBox.show();
    if (ret) {
      try {
        // let ret = await this.fetchDeleteAccountInfo();
        // window.location.href = process.env.DEV_URL_BASE;
      } catch (e) {
        alertStore.dispatch(setAlert(e.message));
      }
      messageBox.hide();
    } else {
      messageBox.hide();
    }
  }

  render() {
    const settings = userSettingStore.getState().userSetting;
    const isApiKeyRegistered = settings.isUserGeminiApiKeyRegistered;
    const aiCodingEnabled = settings.aiCodingEnabled;

    return html`
      <message-box id="delete-account-confirm">
        <div slot="message-slot">
          <p class="caution"><i class="fa-solid fa-triangle-exclamation"></i></p>
          <p style="text-align: center;">本当に削除しますか？</p>
          <p style="text-align: center; font-size:12px;">アカウントを削除すると、作成したアプリケーションはすべて削除されます。(復元できません)</p>
        </div>
        <button slot="ok-slot" class="confirm-ok-button">削除する</button>
        <button slot="cancel-slot" class="confirm-cancel-button">キャンセル</button>
      </message-box>

      <div class="component-content" @click="${this.hide}">
        <div class="setting-content" @click="${e => e.stopPropagation()}">
          <div class="setting-title">設定</div>
          
          <div class="setting-option">
            <label for="aiCodingCheckbox">AI機能を有効にする</label>
            <label class="switch">
              <input type="checkbox" id="aiCodingCheckbox" .checked="${aiCodingEnabled}" @change="${this.aiCodingChange}">
              <span class="slider round"></span>
            </label>
          </div>

          <div class="setting-section">
            <div class="setting-section-title">Gemini APIキー設定</div>
            <p class="setting-description">
              ご自身のGemini APIキーを設定すると、Pwappyポイントの消費を抑えることができます。<br>
              <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer">
                Google AI Studioでキーを取得<i class="fa-solid fa-arrow-up-right-from-square"></i>
              </a>
            </p>
            
            ${isApiKeyRegistered ? html`
              <!-- 登録済みの場合の表示 -->
              <div class="api-key-display">
                <div class="api-key-status">
                  <i class="fa-solid fa-circle-check"></i>
                  <span>APIキーは登録済みです。</span>
                </div>
                <button class="delete-api-key-button setting-button" @click="${this.handleDeleteApiKey}">削除</button>
              </div>
            ` : html`
              <!-- 未登録の場合の表示 -->
              <div class="api-key-form">
                <input type="password" id="gemini-api-key" placeholder="ご自身のAPIキーを入力">
                <button class="save-api-key-button setting-button" @click="${this.handleSaveApiKey}">保存</button>
              </div>
            `}
          </div>
          
          <!-- <button class="setting-button" @click="${this.handleDeleteAccount}">アカウント削除</button> -->
        </div>
      </div>
    `;
  }
}

customElements.define('dashboard-account-setting', DashboardAccountSetting);